import type { DocContent, Language, PageLink } from './types';
import { extractTableOfContents } from '$lib/utils/markdown';
// Build-time manifest of "<lang>/<slug>" -> last git commit date (YYYY-MM-DD),
// generated by scripts/gen-doc-dates.mjs (prebuild). Falls back to today only
// for files with no git history.
import docDates from './doc-dates.json';

// Import all translation files dynamically
const translationModules = import.meta.glob('/src/lib/docs/translations/**/*.md', {
	query: '?raw',
	import: 'default'
});

// Synchronously-derived set of doc slugs that actually have a markdown file.
// Glob keys are available without awaiting the lazy imports, so we can use this
// to filter navigation/cards and never render a link to a missing doc.
export const availableDocSlugs: Set<string> = (() => {
	const slugs = new Set<string>();
	const pattern = /\/src\/lib\/docs\/translations\/[^/]+\/(.+)\.md$/;
	for (const path in translationModules) {
		const match = path.match(pattern);
		if (match) slugs.add(match[1]);
	}
	return slugs;
})();

// Cache for loaded documents
const docCache = new Map<string, DocContent>();

/**
 * Get the documentation navigation order
 */
function getDocumentationOrder(): string[] {
	return [
		// Getting started
		'introduction',
		'quick-start',
		'first-project',
		// Work
		'projects',
		'tasks',
		'task-properties',
		'boards',
		'todo',
		'tags',
		'time-tracking',
		'calendar',
		// Clients & finance
		'clients',
		'client-view',
		'invoicing',
		'cashflow',
		'reports',
		// Team & people
		'teams',
		'team-setup',
		'team-communication',
		'roles-permissions',
		'people',
		'absence',
		'messages',
		'pulse',
		'workspaces',
		// Content & files
		'files',
		'notes',
		'spreadsheet',
		// Integrations
		'integrations',
		// CLI
		'cli/overview',
		'cli/installation',
		'cli/configuration',
		'cli/commands',
		'cli/commands/auth',
		'cli/commands/timer',
		'cli/commands/project',
		'cli/commands/client',
		'cli/commands/log',
		'cli/commands/workspace',
		'cli/workflows',
		'cli/workflows/projects-clients',
		'cli/workflows/team-automation',
		'cli/workflows/advanced',
		'cli/scripting',
		'cli/scripting/shell',
		'cli/scripting/python',
		'cli/scripting/nodejs',
		'cli/scripting/advanced',
		'cli/integrations',
		'cli/best-practices',
		'cli/troubleshooting',
		// Browser extension
		'browser-extension/overview',
		'browser-extension/installation',
		'browser-extension/features',
		'browser-extension/shortcuts',
		// Mobile
		'mobile/overview',
		'mobile/features',
		'mobile/ios',
		'mobile/android',
		'mobile/offline',
		'mobile/notifications',
		// Help
		'faq',
		'troubleshooting',
		'contact-support'
	];
}

/**
 * Load a documentation file for a specific language and slug
 */
export async function loadDocContent(slug: string, language: Language): Promise<DocContent | null> {
	const cacheKey = `${language}/${slug}`;

	// Check cache first
	if (docCache.has(cacheKey)) {
		return docCache.get(cacheKey)!;
	}

	// Handle nested paths - slug already contains forward slashes (e.g., "cli/overview")
	const filePath = slug;

	// Try to load the translated version first
	let path = `/src/lib/docs/translations/${language}/${filePath}.md`;
	let content: string | null = null;

	if (translationModules[path]) {
		try {
			content = (await translationModules[path]()) as string;
		} catch (error) {
			console.warn(`Failed to load translation for ${path}:`, error);
		}
	}

	// Fallback to English if translation not found
	if (!content && language !== 'en') {
		path = `/src/lib/docs/translations/en/${filePath}.md`;
		if (translationModules[path]) {
			try {
				content = (await translationModules[path]()) as string;
				console.info(`Using English fallback for ${slug} (${language} not available)`);
			} catch (error) {
				console.error(`Failed to load English fallback for ${slug}:`, error);
			}
		}
	}

	if (!content) {
		return null;
	}

	// Parse the content to extract metadata
	const lines = content.split('\n');
	const titleMatch = lines[0].match(/^#\s+(.+)$/);
	const title = titleMatch ? titleMatch[1] : slug.replace(/-/g, ' ');

	// Build the meta description from the opening prose. Combine consecutive paragraph
	// lines (skipping headings, lists, tables, code, quotes) until we have a useful
	// length — Ahrefs flags "meta description too short" under ~110 chars — then trim
	// to ~155 at a word boundary. Falls back to whatever prose exists if the doc is short.
	let description = '';
	for (let i = 1; i < lines.length; i++) {
		const line = lines[i].trim();
		const isProse =
			line &&
			!line.startsWith('#') &&
			!line.startsWith('*') &&
			!line.startsWith('-') &&
			!line.startsWith('|') &&
			!line.startsWith('```') &&
			!line.startsWith('>');
		if (isProse) {
			description += (description ? ' ' : '') + line.replace(/\*\*/g, '').replace(/`/g, '');
			if (description.length >= 150) break;
		} else if (description.length >= 110) {
			break; // enough collected — don't bleed into the next section
		}
	}
	if (description.length > 158) {
		description = description.slice(0, 155).replace(/\s+\S*$/, '') + '…';
	}

	// Extract table of contents
	const tableOfContents = extractTableOfContents(content);

	// Get navigation links
	const navigation = getNavigationLinks(slug, language);

	// Create the document content object
	const docContent: DocContent = {
		id: slug,
		title,
		description,
		content,
		lastUpdated:
			(docDates as Record<string, string>)[`${language}/${slug}`] ||
			(docDates as Record<string, string>)[`en/${slug}`] ||
			new Date().toISOString().split('T')[0],
		tags: extractTags(slug),
		tableOfContents,
		language,
		previousPage: navigation.previous,
		nextPage: navigation.next
	};

	// Cache the result
	docCache.set(cacheKey, docContent);

	return docContent;
}

/**
 * Get available languages for a specific document
 */
export async function getAvailableLanguages(slug: string): Promise<Language[]> {
	const languages: Language[] = [];
	const pattern = new RegExp(`/src/lib/docs/translations/([^/]+)/${slug}\\.md$`);

	for (const path in translationModules) {
		const match = path.match(pattern);
		if (match) {
			languages.push(match[1] as Language);
		}
	}

	// Always include English as fallback
	if (!languages.includes('en')) {
		languages.push('en');
	}

	return languages;
}

/**
 * Get all available documentation slugs
 */
export async function getAllDocSlugs(): Promise<string[]> {
	const slugs = new Set<string>();
	const pattern = /\/src\/lib\/docs\/translations\/[^/]+\/(.+)\.md$/;

	for (const path in translationModules) {
		const match = path.match(pattern);
		if (match) {
			slugs.add(match[1]);
		}
	}

	return Array.from(slugs);
}

/**
 * Extract tags based on the document slug
 */
function extractTags(slug: string): string[] {
	const tags: string[] = [];

	// Add category tags based on slug patterns
	if (
		slug.includes('quick-start') ||
		slug.includes('introduction') ||
		slug.includes('installation')
	) {
		tags.push('getting-started');
	}
	if (slug.includes('cli')) {
		tags.push('cli', 'command-line', 'terminal');
	}
	if (slug.includes('project')) {
		tags.push('projects');
	}
	if (slug.includes('task')) {
		tags.push('tasks');
	}
	if (slug.includes('team')) {
		tags.push('teams', 'collaboration');
	}
	if (slug.includes('timer')) {
		tags.push('time-tracking');
	}
	if (slug.includes('client')) {
		tags.push('clients', 'customer-management');
	}
	if (slug.includes('workspace')) {
		tags.push('workspaces', 'organization');
	}
	if (slug.includes('auth')) {
		tags.push('authentication', 'security');
	}
	if (slug.includes('api') || slug.includes('webhook') || slug.includes('integration')) {
		tags.push('developers', 'api');
	}
	if (slug.includes('invoice') || slug.includes('billing')) {
		tags.push('billing', 'invoicing');
	}
	if (slug.includes('time')) {
		tags.push('time-tracking');
	}
	if (slug.includes('script')) {
		tags.push('automation', 'scripting');
	}
	if (slug.includes('workflow')) {
		tags.push('workflows', 'productivity');
	}

	return tags;
}

/**
 * Get previous and next page navigation
 */
function getNavigationLinks(
	currentSlug: string,
	language: Language
): { previous?: PageLink; next?: PageLink } {
	// Only navigate between docs that actually exist, so prev/next never 404.
	const order = getDocumentationOrder().filter((slug) => availableDocSlugs.has(slug));
	const currentIndex = order.indexOf(currentSlug);

	if (currentIndex === -1) {
		return {};
	}

	const result: { previous?: PageLink; next?: PageLink } = {};

	// Get previous page
	if (currentIndex > 0) {
		const prevSlug = order[currentIndex - 1];
		const prevTitle = getPageTitle(prevSlug);
		result.previous = {
			slug: prevSlug,
			title: prevTitle
		};
	}

	// Get next page
	if (currentIndex < order.length - 1) {
		const nextSlug = order[currentIndex + 1];
		const nextTitle = getPageTitle(nextSlug);
		result.next = {
			slug: nextSlug,
			title: nextTitle
		};
	}

	return result;
}

/**
 * Get page title from slug
 */
function getPageTitle(slug: string): string {
	const titles: Record<string, string> = {
		introduction: 'Introduction',
		'quick-start': 'Quick Start',
		installation: 'Installation',
		'first-project': 'Your First Project',
		projects: 'Projects',
		'creating-projects': 'Creating Projects',
		'project-settings': 'Project Settings',
		tasks: 'Tasks',
		'creating-tasks': 'Creating Tasks',
		'task-properties': 'Task Properties',
		'task-views': 'Task Views',
		teams: 'Teams',
		'team-setup': 'Team Setup',
		'roles-permissions': 'Roles & Permissions',
		'team-communication': 'Team Communication',
		'time-tracking': 'Time Tracking',
		invoicing: 'Invoicing & Billing',
		'cli/overview': 'CLI Overview',
		'cli/installation': 'CLI Installation',
		'cli/configuration': 'CLI Configuration',
		'cli/commands': 'CLI Commands',
		'cli/commands/auth': 'Authentication Commands',
		'cli/commands/timer': 'Timer Commands',
		'cli/commands/project': 'Project Commands',
		'cli/commands/client': 'Client Commands',
		'cli/commands/log': 'Log Commands',
		'cli/commands/workspace': 'Workspace Commands',
		'cli/workflows': 'CLI Workflows',
		'cli/scripting': 'CLI Scripting',
		'cli/troubleshooting': 'CLI Troubleshooting',
		'api-overview': 'API Overview',
		webhooks: 'Webhooks',
		integrations: 'Integrations',
		faq: 'FAQ',
		troubleshooting: 'Troubleshooting',
		'contact-support': 'Contact Support'
	};

	return (
		titles[slug] ||
		slug
			.replace(/\//g, ' - ')
			.replace(/-/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase())
	);
}

/**
 * Clear the documentation cache
 */
export function clearDocCache(): void {
	docCache.clear();
}
